import { useNavigate } from 'react-router-dom';
import { highlightText } from '../utils/functions/highlightText';
import useSearch from '../utils/customHooks/useSearch';
import arrowIcon from '../assets/images/Arrow narrow right.svg';
import { EmptyList } from './List/EmptyList';
import emptyIcon from '../assets/images/empty.svg';
import { WithTooltip } from './tooltip.tsx/withTootip';
import uploadDisabled from '../../shared/assets/images/uploadDisabled.svg';
import { Spin } from 'antd';
import { ISearchCandidate } from '../utils/interfaces/candidatesListFace';
import { LoadingOutlined } from '@ant-design/icons';
import ExclamationYellow from '../../shared/assets/images/ExclamationYellow.svg';
import { Warning } from './Warning/Warning';
import { useDispatch } from 'react-redux';
import { setIsSubmittingSearch } from '../../modules/createJob/slice/candidateSlice';

const SearchResult = ({ searchInput, className, useSeeAll = true, onResultClick, resultRef }: { searchInput: string; className?: string; useSeeAll?: boolean; onResultClick: () => void; resultRef?: React.RefObject<HTMLDivElement>; }) => {
    const { results, isLoading, error, totalCandidatesCount } = useSearch(searchInput);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleResultClick = () => {
        onResultClick();
        navigate(`/search?query=${searchInput}`);
        dispatch(setIsSubmittingSearch(true))
    };

    const handleClickOnCandidate = (candidate: ISearchCandidate) => {
        onResultClick();
        navigate(`/jobs/${candidate?.jobId}/candidates/${candidate?.id}`, { state: { fromSearch: true } })
    }

    const handleTooltipClick = (e: React.MouseEvent, candidateId: number) => {
        e.stopPropagation();
        navigate(`/similarApplication/${candidateId}`);
    };

    return (
        <div
            ref={resultRef}
            className={`p-2 z-10 absolute left-0 space-y-3 shadow-sm ring-1 ring-slate-400 ring-opacity-10 bg-white top-10 rounded-xl w-full max-h-fit ${className}`}

        >
            {isLoading ? <div className='text-center'><Spin className='my-10' indicator={<LoadingOutlined spin />} /></div> :
                error ? <Warning title='Something went wrong!' withHomeBtn={false} withRefreshBtn={false} />
                    : results?.length > 0 ? (
                        <>
                            <div className='px-3 pt-3 text-xs font-normal text-gray-600'>
                                Sort by Best Match
                            </div>
                            {results?.slice(0, 4).map((result: any) => (
                                <div
                                    key={result?.id}
                                    onClick={() => handleClickOnCandidate(result)}
                                    className='p-3 rounded-md cursor-pointer hover:bg-indigo-50'
                                >
                                    <div className='text-base font-bold text-gray-600'>
                                        {result?.firstName ? highlightText(
                                            `${result?.firstName} ${result?.lastName}`,
                                            searchInput
                                        ) : result?.cvFileName}
                                        <span>
                                            {result?.batchId &&
                                                <WithTooltip
                                                    className='w-36'
                                                    placement='right'
                                                    title='This candidate was uploaded by admin'
                                                >
                                                    <img src={uploadDisabled} alt='This candidate was uploaded by admin' className='inline ms-3' />
                                                </WithTooltip>
                                            }
                                        </span>
                                        {result?.isSimilar && <WithTooltip
                                            className='w-36'
                                            placement='right'
                                            colorText='#B45309'
                                            backgroundColor='#FEF3C7'
                                            width='w-48'

                                            title={<>Similarity detection: It appears that the same candidate has applied for multiple positions.
                                                <h4 className='mt-3 cursor-pointer' onClick={(e) => handleTooltipClick(e, result?.id)}>Click to compare.</h4></>}
                                        >
                                            <img src={ExclamationYellow} alt='Similarity detection' onClick={(e) => handleTooltipClick(e, result?.id)} className='inline ms-3' />
                                        </WithTooltip>}
                                    </div>
                                    <p className='text-sm font-normal text-gray-600'>
                                        {highlightText(result?.currentPosition, searchInput)}
                                    </p>
                                    <p className='text-sm font-normal text-gray-600'>
                                        in {result?.title} / {highlightText(result?.stageName, searchInput)}
                                    </p>
                                    {result?.email && result?.email?.toLowerCase()?.includes(searchInput?.toLowerCase() ?? '') && (
                                        <p className='text-sm font-normal text-gray-600'>
                                            {highlightText(result?.email, searchInput)}
                                        </p>
                                    )}
                                    <p className='overflow-hidden text-sm font-normal text-gray-400 text-ellipsis whitespace-nowrap'>
                                        {result?.snippet ? <>..{highlightText(result?.snippet, searchInput)}..</> : null}
                                    </p>
                                </div>
                            ))}
                        </>
                    ) : (
                        searchInput && (
                            <EmptyList
                                title='No matching results'
                                description='Try using different keywords to find who you are looking for.'
                                image={emptyIcon}
                                className='searchEmptyList'
                            />
                        )
                    )}
            {useSeeAll && !error && (
                <div
                    onClick={handleResultClick}
                    className='px-4 flex justify-between border-t-[0.01rem] border-gray-100 pb-2 pt-3 cursor-pointer mt-3'
                >
                    <div className='font-semibold text-indigo-700'>
                        {results?.length
                            ? `view all ${totalCandidatesCount} candidates`
                            : 'Go to candidates'}
                    </div>
                    <img alt='arrow' src={arrowIcon} />
                </div>
            )}
        </div>
    );
};

export default SearchResult;
